import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Column, format, getIsUserSignedIn, getUser, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Link } from "@gaming/xds-react-js-icons";

import { learnAboutMsaUrl } from "../../constants";
import { signInUrl, switchAccountUrl } from "../../constants/urlsClientSide";
import { useProgramBackgroundColorPicker } from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls, Paths } from "../../partnerConfig/routes";
import { signUpUrl } from "../../server/partnerCenter/constants/urls";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import { ContentBox, ContentBoxIcon } from "../ContentBox";
import { FormCard } from "../FormCard";
import { RightSideWrapper } from "../RightSideWrapper";
import { TextLink } from "../TextLink";
import { ViewButton } from "../ViewButton";
import * as styles from "./AccountManager.module.css";

type AccountManagerProps = {
  isNdaUnderReviewStepOrAfter: boolean;
  hideCompanyInformationRow?: boolean;
  program?: Programs;
};

const SignedInComponent = ({
  isNdaUnderReviewStepOrAfter,
  program,
  hideCompanyInformationRow,
}: AccountManagerProps) => {
  const [welcomeUserFormat, switchAccount, companyInformation] = useStrings(
    "welcomeUserFormat",
    "switchAccount",
    "companyInformation"
  );
  const userInfo = useSelector(getUser);
  const history = useHistory();

  const moveToIntroPreview = () => {
    logPageAction(OnboardingPageAction.ViewIntroductionLink);
    switch (program) {
      case Programs.Aaa:
        history.push(OnboardingUrls[RouteNames.AaaFormPreview], {
          applicationId: Paths.IntroInfo,
        });
        break;
      case Programs.IdXbox:
        history.push(OnboardingUrls[RouteNames.IdXboxFormPreview], {
          applicationId: Paths.IntroInfo,
        });
        break;
      case Programs.Pitch:
        history.push(OnboardingUrls[RouteNames.PitchFormPreview]);
        break;
    }
  };
  return (
    <ContentBox
      titleConfiguration={{
        title: format(welcomeUserFormat, userInfo?.firstName ?? ""),
        rightSideButton: {
          text: switchAccount,
          onClick: () => {
            window.location.href = switchAccountUrl;
            logPageAction(OnboardingPageAction.SwitchAccountLink);
          },
          icon: ContentBoxIcon.ArrowsLeftRightSwap,
        },
      }}
    >
      <p className={styles.emailContainer}>
        <small>{userInfo?.emailAddress}</small>
      </p>
      {isNdaUnderReviewStepOrAfter && program && !hideCompanyInformationRow && (
        <div style={{ display: "flex" }}>
          <h3 className={styles.companyInformation}>{companyInformation}</h3>
          <ViewButton onClick={moveToIntroPreview} />
        </div>
      )}
    </ContentBox>
  );
};

const SignedOutComponent = () => {
  const [signInTitle, signInSubtitle, learnAboutMsa, notStarted, createAccount, signIn] =
    useStrings(
      "signInTitle",
      "signInSubtitle",
      "learnAboutMsa",
      "notStarted",
      "createAccount",
      "signIn"
    );
  const signUpOnClick = () => {
    logPageAction(OnboardingPageAction.CreateAccountButton);
    window.open(signUpUrl, "_blank");
  };
  const signInOnClick = () => {
    logPageAction(OnboardingPageAction.SignInButton);
    window.location.href = signInUrl;
  };
  const colorStyle = useProgramBackgroundColorPicker(true);
  return (
    <ContentBox
      titleConfiguration={{
        title: signInTitle,
        rightSideText: notStarted,
        icon: ContentBoxIcon.GreenCircle,
        separatorBar: true,
      }}
    >
      <Row className={styles.containerRow}>
        <Column className={styles.subtitleColumn}>
          <p className={styles.signInSubtitle}>{signInSubtitle}</p>
          <div className={styles.learnMoreMsa}>
            <TextLink href={learnAboutMsaUrl} className={styles.learnMoreMsaLink}>
              {learnAboutMsa}
            </TextLink>
            <Icon aria-hidden="true" icon={Link} iconSize={IconSize.Custom} />
          </div>
        </Column>
        <Column>
          <BasicButton
            className={styles.createAccountButton}
            as={"button"}
            buttonType={ButtonType.Tertiary}
            onClick={signUpOnClick}
            textDecorationUnderline={true}
          >
            {createAccount}
          </BasicButton>
        </Column>
        <Column>
          <BasicButton
            className={colorStyle}
            as={"button"}
            buttonType={ButtonType.Brand}
            onClick={signInOnClick}
            textDecorationUnderline={true}
          >
            {signIn}
          </BasicButton>
        </Column>
      </Row>
    </ContentBox>
  );
};

export const AccountManager = (props: AccountManagerProps) => {
  const isSignedIn = useSelector(getIsUserSignedIn);
  if (isSignedIn) {
    return <SignedInComponent {...props} />;
  }

  return (
    <RightSideWrapper rightSideComponent={<FormCard cardId={"AccountManagerSignedOut"} />}>
      <SignedOutComponent />
    </RightSideWrapper>
  );
};
